/**
 * NameTheSquare Mini-Game
 *
 * Self-contained game: Engine + Definition + Registration
 *
 * Rules:
 * - Mode 1 (highlight): Squares are highlighted, player types their coordinates
 * - Mode 2 (findPiece): A position is shown, player types which square a piece is on
 * - Win: Correctly name all target squares
 * - Fail: Attempt limit exceeded (if set)
 */
import { MiniGameBase } from "../MiniGameBase";
import { MiniGameRegistry } from "../MiniGameRegistry";
import * as BoardUtils from "../BoardUtils";
import { isValidSquare } from "../BoardUtils";
// ========================================
// Helper Functions
// ========================================
const VALID_PIECE_TYPES = ["k", "q", "r", "b", "n", "p"];
const PIECE_NAMES = {
    k: "king",
    q: "queen",
    r: "rook",
    b: "bishop",
    n: "knight",
    p: "pawn",
};
function parsePieceString(piece) {
    if (!piece || piece.length < 2)
        return null;
    const color = piece[0].toLowerCase();
    const type = piece[1].toLowerCase();
    if (color !== "w" && color !== "b")
        return null;
    if (!VALID_PIECE_TYPES.includes(type))
        return null;
    return { type, color };
}
function getPieceName(type, color) {
    return `${color === "w" ? "white" : "black"} ${PIECE_NAMES[type]}`;
}
function normalizeSquare(square) {
    return square.trim().toLowerCase();
}
// ========================================
// Engine
// ========================================
export class NameTheSquareEngine extends MiniGameBase {
    constructor(config) {
        // If findPiece mode, resolve the piece's square(s) and inject them as targets.
        const findPieceStr = config.params?.findPiece;
        if (findPieceStr && (!config.targets || config.targets.length === 0)) {
            const parsed = parsePieceString(findPieceStr);
            if (parsed) {
                const board = BoardUtils.parseFenToBoard(config.fen);
                const matches = board?.pieces.filter((p) => p.type === parsed.type && p.color === parsed.color) || [];
                if (matches.length > 0) {
                    config = { ...config, targets: matches.map((p) => p.square) };
                }
            }
        }
        super(config);
    }
    initializeState() {
        const board = BoardUtils.parseFenToBoard(this.config.fen);
        if (board)
            this.pieces = board.pieces;
        const targets = this.config.targets || [];
        const isHighlightMode = !this.config.params?.findPiece;
        return {
            fen: this.config.fen,
            moves: [],
            moveCount: 0,
            targetsAchieved: 0,
            totalTargets: targets.length,
            remainingTargets: [...targets],
            foundSquares: [],
            wrongSquares: [],
            status: "playing",
            highlightSquares: isHighlightMode ? [...targets] : [],
        };
    }
    getValidMoves(_square) {
        return [];
    }
    /**
     * Handle a text answer submission.
     * The player types the name of a square (e.g. "e4").
     */
    handleInput(text) {
        if (this.state.status !== "playing") {
            return { valid: false, error: "Game is already over" };
        }
        const answer = normalizeSquare(text);
        if (!isValidSquare(answer)) {
            return { valid: false, error: "Please enter a valid square (e.g., e4)" };
        }
        this.state.moveCount++;
        this.state.moves.push({ from: answer, to: answer });
        // Check if answer matches any remaining target
        const isCorrect = this.state.remainingTargets.some((t) => normalizeSquare(t) === answer);
        if (isCorrect) {
            this.state.targetsAchieved++;
            this.state.foundSquares = [...this.state.foundSquares, answer];
            this.state.remainingTargets = this.state.remainingTargets.filter((t) => normalizeSquare(t) !== answer);
            this.state.highlightSquares = this.state.highlightSquares.filter((t) => normalizeSquare(t) !== answer);
            if (this.state.remainingTargets.length === 0) {
                this.state.status = "complete";
                return {
                    valid: true,
                    targetHit: true,
                    targetSquare: answer,
                    gameComplete: true,
                };
            }
            return { valid: true, targetHit: true, targetSquare: answer };
        }
        // Wrong answer
        if (!this.state.wrongSquares.includes(answer)) {
            this.state.wrongSquares = [...this.state.wrongSquares, answer];
        }
        if (this.isMoveLimitExceeded()) {
            this.state.status = "failed";
            return {
                valid: true,
                targetHit: false,
                gameFailed: true,
                error: "Too many attempts!",
            };
        }
        return { valid: true, targetHit: false, error: "Incorrect! Try again." };
    }
    /**
     * makeMove delegates to handleInput using `from` as the text answer.
     * NameTheSquare has no piece movement — the (from, to) signature does not apply.
     */
    makeMove(from, _to) {
        return this.handleInput(from);
    }
    getHint() {
        if (this.state.status !== "playing" ||
            this.state.remainingTargets.length === 0)
            return null;
        const target = this.state.remainingTargets[Math.floor(Math.random() * this.state.remainingTargets.length)];
        return { from: target, to: target };
    }
}
// ========================================
// Registration
// ========================================
MiniGameRegistry.register({
    type: "NAME_THE_SQUARE",
    createEngine: (config) => new NameTheSquareEngine(config),
    validate: (config) => {
        const errors = [];
        const findPieceStr = config.params?.findPiece;
        if ((!config.targets || config.targets.length === 0) && !findPieceStr) {
            errors.push("Requires target squares (targets=e4 or targets=e4,d5) or a piece to find (findPiece=wQ)");
            return { valid: false, errors };
        }
        if (findPieceStr) {
            const parsed = parsePieceString(findPieceStr);
            if (!parsed) {
                errors.push('Invalid findPiece format. Use "wQ" (white queen) or "bR" (black rook)');
            }
            else {
                const board = BoardUtils.parseFenToBoard(config.fen);
                if (!board?.pieces.some((p) => p.type === parsed.type && p.color === parsed.color)) {
                    errors.push(`No ${getPieceName(parsed.type, parsed.color)} found in position`);
                }
            }
        }
        if (config.targets) {
            for (const target of config.targets) {
                if (!isValidSquare(target))
                    errors.push(`Invalid target square: ${target}`);
            }
        }
        return { valid: errors.length === 0, errors };
    },
    getInstructionText: (config) => {
        const findPieceStr = config.params?.findPiece;
        if (findPieceStr) {
            const parsed = parsePieceString(findPieceStr);
            if (parsed) {
                const count = config.targets?.length || 1;
                const name = getPieceName(parsed.type, parsed.color);
                if (count > 1)
                    return `Which squares are the ${count} ${name}s on?`;
                return `Which square is the ${name} on?`;
            }
        }
        const targets = config.targets || [];
        if (targets.length === 0)
            return "No target squares specified.";
        if (targets.length === 1)
            return "Type the name of the highlighted square";
        return `Name all ${targets.length} highlighted squares`;
    },
    supportsHints: true,
    interactionType: "input",
});
